<script>
  import { MIN_TASK_EXECUTIONS } from "lib/util/taskThresholds.js";
  import { SectionWrapper } from "paxray-components-svelte";
  import Header from "lib/components/Header.svelte";
  import MatrixTable from "./components/MatrixTable.svelte";
  import { filtersConfigStore } from "@stores/damFiltersConfigStores.js";
  import Filter from "./components/filter/Filter.svelte";
  import { Button } from "paxray-components-svelte";
  import PaxbotChat from "lib/components/paxbot/PaxbotChat.svelte";
  import { ROUTES } from "@utils/routes.js";
  import { uiFeatureStore } from "@stores/uiFeatureStore.js";

  import { fetchWithSession } from "../../../../lib/util/fetchWithSession.js";
  import { createRequestScope } from "lib/util/requestScope.js";
  import LFilters from "../components/LFilters.svelte";
  import { recordingStore } from "@stores/recordingStore.js";


  // Requests der Maske enden, wenn die Maske verlassen wird.
  const abortSignal = createRequestScope();
  export let idproject;

  const { setCurrentView } = filtersConfigStore;

  let view = "KPI Matrix";
  let applicationlist = [];
  let loading = true;
  let header = [
    "Task",
    "Cost p.a.",
    "Time p.a.",
    "Frequency p.a.",
    "Avg. Time",
    "Median Time",
    "Cost/h",
    "Std. Cases",
    "Top App Ratio",
    "Open Task",
  ];
  let rows = [];
  let loadedProject = "";
  let selectedTeam = "";
  let selectedProcess = "";
  let selectedSubprocess = "";
  let selectedTask = "";
  let selectedSubtask = "";
  let sortColumn = 0;
  let sortDirection = "asc";
  let columnWidths = [];
  const sortKeys = [
    "l4",
    "cost_pa",
    "time_pa",
    "frequency_pa",
    "avg_time",
    "median_time",
    "cost_per_hour",
    "standard_case_share",
    "top_app_ratio",
  ];

  let paxbotOpen = false;
  let lFiltersRef;
  const paxbotStarterQuestions = [
    "What should I look at first?",
    "Explain this view.",
    "Which tasks stand out most?",
  ];

  $: setCurrentView(view);
  $: columnWidths = getColumnWidths(header);

  $: if (
    idproject &&
    $filtersConfigStore.storeLoaded &&
    loadedProject !== idproject
  ) {
    loadedProject = idproject;
    getWindowPartitions();
  }
  $: teamOptions = getTeamOptions(applicationlist);
  // Gleiche Mindestzahl wie die Aufgabenauswahl des Process Explorers:
  // Aufgaben mit weniger Registrierungen erscheinen nicht in der Matrix.
  $: rowsForSelectedTeam = applicationlist.filter(
    (row) =>
      rowMatchesTeam(row, selectedTeam) &&
      (row.nrecordings == null || Number(row.nrecordings) >= MIN_TASK_EXECUTIONS),
  );
  $: selectedProcessName = selectedProcess
    ? $recordingStore.processes?.find((p) => p.id === selectedProcess)?.name ?? null
    : null;
  $: selectedSubprocessName = selectedSubprocess
    ? $recordingStore.listOfSubprocesses?.find((sp) => sp.id === selectedSubprocess)?.name ?? null
    : null;
  $: selectedTaskName = selectedTask
    ? $recordingStore.listOfTasks?.find((t) => t.id === selectedTask)?.name ?? null
    : null;
  $: selectedSubtaskName = selectedSubtask
    ? $recordingStore.listOfSubtasks?.find((st) => st.id === selectedSubtask)?.name ?? null
    : null;
  $: filteredApplications = rowsForSelectedTeam.filter(
    (row) =>
      (!selectedProcessName || row.l1 === selectedProcessName) &&
      (!selectedSubprocessName || row.l2 === selectedSubprocessName) &&
      (!selectedTaskName || row.l3 === selectedTaskName) &&
      (!selectedSubtaskName || row.l4 === selectedSubtaskName),
  );
  $: rows = setRows(
    sortApplications(
      filteredApplications,
      sortKeys[sortColumn] || "l4",
      sortDirection,
    ),
  );
  $: noDataForFilters = !loading && filteredApplications.length === 0;
  // No task reaches the registration minimum yet, so filters cannot help
  $: noMatrixData =
    !loading &&
    !applicationlist.some(
      (row) =>
        row.nrecordings == null || Number(row.nrecordings) >= MIN_TASK_EXECUTIONS,
    );

  function getWindowPartitions() {
    loading = true;
    fetchWithSession(`/api/damprojects/taskKPIMatrix?project_id=${idproject}`, { signal: abortSignal })
      .then((res) => res.json())
      .then((data) => {
        applicationlist = data;
        loading = false;
      });
  }

  function setRows(applicationlist) {
    const bodyRows = applicationlist.map((app) => {
      return [
        app.l4,
        formatCost(app.cost_pa),
        formatMatrixDuration(Number(app.time_pa)),
        formatCompactNumber(app.frequency_pa),
        formatMatrixDuration(Number(app.avg_time)),
        formatMatrixDuration(Number(app.median_time)),
        formatCost(app.cost_per_hour),
        formatOptionalPercentage(app.standard_case_share),
        formatTopApp(app),
        getTaskExplorerLink(app),
      ];
    });
    return [...bodyRows, getSummaryRow(applicationlist)];
  }

  function formatCost(value) {
    return `${Math.round(Number(value) || 0).toLocaleString("de-DE")} €`;
  }

  function formatMatrixDuration(value) {
    const secondsTotal = Math.max(0, Math.floor(Number(value) || 0));
    const hours = Math.floor(secondsTotal / 3600);
    const minutes = Math.floor((secondsTotal % 3600) / 60);
    const seconds = secondsTotal % 60;
    if (hours > 0) return `${hours}:${String(minutes).padStart(2, "0")} h`;
    return `${minutes}:${String(seconds).padStart(2, "0")} min`;
  }

  function formatNumber(value) {
    return (Number(value) || 0).toFixed(1);
  }

  function formatOptionalPercentage(value) {
    return value === "" || value == null
      ? "-"
      : `${Math.round(Number(value))}%`;
  }

  function formatTopApp(app) {
    if (!app.top_app || app.top_app_ratio == null) return "-";
    return `${app.top_app} (${Math.round(Number(app.top_app_ratio) * 100)}%)`;
  }

  function formatCompactNumber(value) {
    const number = Number(value) || 0;
    if (Math.abs(number) >= 1_000_000)
      return `${(number / 1_000_000).toFixed(2)} M`;
    if (Math.abs(number) >= 1_000) return `${(number / 1_000).toFixed(1)} k`;
    return Math.round(number);
  }

  function sumBy(sourceRows, key) {
    return sourceRows.reduce((sum, row) => sum + (Number(row[key]) || 0), 0);
  }

  function avgBy(sourceRows, key) {
    return sourceRows.length ? sumBy(sourceRows, key) / sourceRows.length : 0;
  }

  function avgOptionalBy(sourceRows, key) {
    const values = sourceRows
      .map((row) => row[key])
      .filter((value) => value !== "" && value != null);
    return values.length
      ? values.reduce((sum, value) => sum + Number(value), 0) / values.length
      : null;
  }

  function getSummaryRow(sourceRows) {
    return [
      "Total",
      formatCost(sumBy(sourceRows, "cost_pa")),
      formatMatrixDuration(sumBy(sourceRows, "time_pa")),
      formatCompactNumber(sumBy(sourceRows, "frequency_pa")),
      formatMatrixDuration(avgBy(sourceRows, "avg_time")),
      formatMatrixDuration(avgBy(sourceRows, "median_time")),
      formatCost(avgBy(sourceRows, "cost_per_hour")),
      formatOptionalPercentage(
        avgOptionalBy(sourceRows, "standard_case_share"),
      ),
      "-",
      "",
    ];
  }

  function toSelectOptions(options) {
    return options.map((option) => ({ key: option, value: option }));
  }

  function getUniqueOptions(sourceRows, key) {
    return [...new Set(sourceRows.map((row) => row[key]).filter(Boolean))].sort(
      (a, b) => a.localeCompare(b),
    );
  }

  function getTeamOptions(sourceRows) {
    const teamsById = new Map();
    sourceRows.forEach((row) => {
      (row.teamIds || []).forEach((teamId, index) => {
        if (!teamId) return;
        teamsById.set(String(teamId), row.teams?.[index] || "Team");
      });
    });
    return [...teamsById.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  function clearFilters() {
    selectedTeam = "";
    selectedProcess = "";
    selectedSubprocess = "";
    selectedTask = "";
    selectedSubtask = "";
  }

  function rowMatchesTeam(row, team) {
    return (
      !team ||
      (row.teamIds || [])
        .map((teamId) => String(teamId))
        .includes(String(team))
    );
  }

  function sortApplications(sourceRows, key, direction) {
    const numericColumns = new Set([
      "cost_pa",
      "time_pa",
      "frequency_pa",
      "avg_time",
      "median_time",
      "cost_per_hour",
      "standard_case_share",
      "top_app_ratio",
    ]);
    return [...sourceRows].sort((a, b) => {
      const modifier = direction === "desc" ? -1 : 1;
      if (numericColumns.has(key)) {
        return ((Number(a[key]) || 0) - (Number(b[key]) || 0)) * modifier;
      }
      return (
        String(a[key] || "").localeCompare(String(b[key] || "")) * modifier
      );
    });
  }

  function openTaskFrequencyMapping() {
    window.open(
      `${ROUTES.dam.taskfrequencymapping}?idproject=${idproject}`,
      "_blank",
    );
  }

  function getTaskExplorerLink(app) {
    return {
      type: "link",
      href: `${ROUTES.dam.analysis.workflow}?idproject=${idproject}&subtaskid=${app.id}`,
      title: "Open in Task Explorer",
      icon: "/img/assistant/icons/open_in_new.svg",
    };
  }

  function getColumnWidths(headers) {
    const weights = headers.map((head, index) => {
      if (index === 0) return 2.2;
      if (index === 7) return 1;
      if (index === 8) return 1.7;
      if (index === headers.length - 1) return 0.75;
      return Math.max(1, Math.min(1.45, String(head).length / 10));
    });
    const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);
    return weights.map(
      (weight) => `${((weight / totalWeight) * 100).toFixed(2)}%`,
    );
  }

  function getPaxbotRows(question = "") {
    const normalizedQuestion = String(question).toLowerCase();
    const sortedRows = sortApplications(
      filteredApplications,
      sortKeys[sortColumn] || "l4",
      sortDirection,
    );
    const relevantRows = sortedRows.filter(
      (row) =>
        row.l4 && normalizedQuestion.includes(String(row.l4).toLowerCase()),
    );
    const unique = new Map();
    [...relevantRows, ...sortedRows].forEach((row) => {
      if (!row.l4 || unique.has(row.l4)) return;
      unique.set(row.l4, {
        task: row.l4,
        processArea: row.l1 || null,
        subprocess: row.l2 || null,
        taskGroup: row.l3 || null,
        teams: row.teams || [],
        costPerYear: `${Math.round(Number(row.cost_pa) || 0).toLocaleString("de-DE")} EUR`,
        timePerYear: formatMatrixDuration(row.time_pa),
        frequencyPerYear: formatCompactNumber(row.frequency_pa),
        averageTime: formatMatrixDuration(row.avg_time),
        medianTime: formatMatrixDuration(row.median_time),
        costPerHour: `${Math.round(Number(row.cost_per_hour) || 0).toLocaleString("de-DE")} EUR`,
        standardCaseShare: formatOptionalPercentage(row.standard_case_share),
        topApplication: row.top_app || null,
        topApplicationShare:
          row.top_app_ratio == null
            ? null
            : `${Math.round(Number(row.top_app_ratio) * 100)}%`,
      });
    });
    return [...unique.values()].slice(0, 40);
  }

  function getPaxbotContext(question = "") {
    return {
      page: "KPI Matrix",
      projectId: idproject,
      filters: {
        team:
          teamOptions.find((team) => team.id === selectedTeam)?.name ||
          "All teams",
        processArea: selectedProcessName || "All process areas",
        subprocess: selectedSubprocessName || "All subprocesses",
        taskGroup: selectedTaskName || "All task groups",
        task: selectedSubtaskName || "All tasks",
      },
      sorting: {
        column: header[sortColumn],
        direction: sortDirection,
      },
      visibleTaskCount: filteredApplications.length,
      taskNames: filteredApplications.map((row) => row.l4),
      tasks: getPaxbotRows(question),
      totals: {
        costPerYear: `${Math.round(sumBy(filteredApplications, "cost_pa")).toLocaleString("de-DE")} EUR`,
        timePerYear: formatMatrixDuration(
          sumBy(filteredApplications, "time_pa"),
        ),
        frequencyPerYear: formatCompactNumber(
          sumBy(filteredApplications, "frequency_pa"),
        ),
        averageTime: formatMatrixDuration(
          avgBy(filteredApplications, "avg_time"),
        ),
        medianTime: formatMatrixDuration(
          avgBy(filteredApplications, "median_time"),
        ),
        averageCostPerHour: `${Math.round(
          avgBy(filteredApplications, "cost_per_hour"),
        ).toLocaleString("de-DE")} EUR`,
        averageStandardCaseShare: formatOptionalPercentage(
          avgOptionalBy(filteredApplications, "standard_case_share"),
        ),
      },
    };
  }

  async function requestPaxbotAnswer(question, history, responseLanguage) {
    const response = await fetchWithSession("/api/damprojects/paxbot/chat", { signal: abortSignal,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        question,
        history: history.slice(-2),
        context: {
          ...getPaxbotContext(question),
          responseLanguage,
        },
      }),
    });
    const responseText = await response.text();
    let result = {};
    try {
      result = responseText ? JSON.parse(responseText) : {};
    } catch {
      throw new Error(
        `Paxbot endpoint returned ${response.status || "an invalid response"}`,
      );
    }
    if (!response.ok || result.status === "error") {
      throw new Error(result.message || "Paxbot request failed");
    }
    return result.answer;
  }
</script>

<Header
  paddingRight={false}
  section="KPI"
  area="Workflow Analysis"
  breadcrumb="KPI Matrix"
  tooltipText="The KPI Matrix compares tasks by cost, time, frequency, duration, and application or window usage."
>
  <Filter allFiltersDisabled hideProcessFlow />
</Header>

<SectionWrapper
  splitScreen={true}
  fullscreen={!noDataForFilters || noMatrixData}
  seamless={noMatrixData}
  seamlessPadding={noMatrixData}
  loading={loading}
  loadingTitle="Loading KPI data."
  loadingHint="Please wait while the KPI Matrix is prepared."
  emptyState={noDataForFilters}
>
  <main slot="main" class="main">
    <PaxbotChat
      bind:open={paxbotOpen}
      heading="Pax AI"
      subheading="KPI Matrix Assistant"
      starterQuestions={paxbotStarterQuestions}
      requestAnswer={requestPaxbotAnswer}
    />
    <div class="actions">
      <LFilters
        bind:this={lFiltersRef}
        {idproject}
        bind:selectedTeam
        bind:selectedProcess
        bind:selectedSubprocess
        bind:selectedTask
        bind:selectedSubtask
      />
      <div class="top_right_actions">
        {#if $uiFeatureStore.kpiMatrix.taskKpiSetup}
          <div class="task_frequency_button">
            <Button
              text="Task KPI Setup"
              kind="secondary"
              size="field"
              on:click={openTaskFrequencyMapping}
            />
          </div>
        {/if}
        <button
          type="button"
          class="paxbot_canvas_button"
          class:active={paxbotOpen}
          title="Pax AI"
          aria-label="Open Paxbot"
          on:click={() => (paxbotOpen = !paxbotOpen)}
        >
          <img src="/icons/bot.svg" alt="" />
        </button>
      </div>
    </div>

    <MatrixTable
      {header}
      {rows}
      {loading}
      sortable
      bind:sortColumn
      bind:sortDirection
      nonSortableColumns={[9]}
      {columnWidths}
      marginBottom="0rem"
      summaryLastRow
      variant="modern"
    ></MatrixTable>
  </main>
  <div slot="emptyState" class="empty_state_text">
    {#if noMatrixData}
      <div class="empty_state_title">
        At least {MIN_TASK_EXECUTIONS} registrations per task are needed.
      </div>
      <div class="empty_state_hint">
        Tasks appear here once they have enough registrations.
      </div>
    {:else}
      <div class="empty_state_title">
        No data is available for this view with the selected filters.
      </div>
      <div class="empty_state_hint">
        Remove or adjust the filters to update the results.
      </div>
      <div class="empty_state_action">
        <Button text="Reset filters" kind="secondary" on:click={clearFilters} />
      </div>
    {/if}
  </div>
</SectionWrapper>

<style>
  .main {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: 100%;
    height: 100%;
    min-height: 0;
    z-index: 0;
  }

  .main :global(.table-container.modern) {
    flex: 1 1 auto;
    min-height: 0;
  }

  .empty_state_title,
  .empty_state_hint {
    font-size: 1rem;
  }

  .empty_state_title {
    color: var(--pax-black);
  }

  .empty_state_hint {
    color: var(--pax-grey);
    margin-top: 0.5rem;
  }

  .empty_state_action {
    display: flex;
    justify-content: center;
    margin-top: 1rem;
  }

  .actions {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 0.75rem;
  }
/* 
  .filters {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 0.5rem;
    min-width: 0;
    flex: 0 1 auto;
  }

  .filter_field {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    width: 10rem;
    min-width: 0;
  }

  .filter_label {
    color: var(--pax-black);
    font-size: 0.75rem;
    line-height: 1rem;
  }

  .filter_field :global(.custom_select) {
    width: 100%;
  }

  .filter_field :global(.bx--form-item) {
    width: 100%;
  }

  .filter_field :global(.bx--select-input) {
    width: 100%;
  } */

  .task_frequency_button {
    flex: 0 0 auto;
  }

  .top_right_actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 0 0 auto;
  }

  .paxbot_canvas_button {
    height: 3rem;
    width: 3rem;
    border: 0;
    border-radius: 0.5rem;
    background: var(--pax-black);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: 300ms ease-in-out;
    overflow: hidden;
    padding: 0;
  }

  .paxbot_canvas_button.active {
    background: var(--pax-orange);
  }

  .paxbot_canvas_button:hover {
    background: #2a2a2f;
  }

  .paxbot_canvas_button img {
    width: 1.25rem;
    height: 1.25rem;
    object-fit: contain;
    filter: brightness(0) invert(1);
  }
</style>
